import React, { useState, useEffect } from "react";
import {
	Card,
	Table,
	Form,
	Button,
	Row,
	Col,
	Spinner,
	Alert,
	DatePicker,
} from "react-bootstrap";
import moment from "moment";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";

const FinancialFunnel = () => {
	const [financialData, setFinancialData] = useState([]);
	const [monthlyData, setMonthlyData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [startDate, setStartDate] = useState(
		moment().subtract(6, "days").format("YYYY-MM-DD"),
	);
	const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
	const [adSpendInputs, setAdSpendInputs] = useState({});

	// Read initial toggle states from URL params so a refresh restores the last set state
	const initialParams = new URLSearchParams(window.location.search);
	const paramBool = (key, def) =>
		initialParams.has(key) ? initialParams.get(key) === "1" : def;

	// Revenue source toggles (all on by default)
	const [showRenewal, setShowRenewal] = useState(paramBool("renewal", true));
	const [showMonthly, setShowMonthly] = useState(paramBool("subscription", true));
	const [showTopup, setShowTopup] = useState(paramBool("topup", true));

	// When on, estimate refunds and chargebacks at 5% of gross each (instead of the default 15%)
	const [lowRiskRates, setLowRiskRates] = useState(paramBool("lowRisk", false));
	const riskRate = lowRiskRates ? 0.05 : 0.15;

	// When on, exclude the 10% ad cost fee from all calculations
	const [noAdFees, setNoAdFees] = useState(paramBool("noAdFees", false));
	const adFeeRate = noAdFees ? 0 : 0.1;

	// When on, project M1-M4 as if the cohort retained 40% after month 1
	// (scales the observed 27.5%/18.3%/13.1%/10.9% curve so M1 = 40%, same decay shape)
	const [simRetention, setSimRetention] = useState(paramBool("simRetention", false));
	const baseCurve = [0.275, 0.183, 0.131, 0.109];
	const retentionCurve = simRetention
		? baseCurve.map((r) => r * (0.4 / 0.275))
		: baseCurve;

	// Helper function to check if a date is today
	const isToday = (dateString) => {
		return moment(dateString).isSame(moment(), "day");
	};

	// Fetch financial data for the date range
	const fetchFinancialData = async () => {
		try {
			setLoading(true);
			setError("");

			const renewal = showRenewal ? 'true' : 'false';
			const subscription = showMonthly ? 'true' : 'false';
			const topup = showTopup ? 'true' : 'false';
			const response = await axiosClient.get(
				"https://api.viralgpt.com/admin/financial-range",
				{
					params: { startDate, endDate, renewal, subscription, topup },
				},
			);

			// Always fetch subscription-only data for Total Estimated Revenue calculations
			const monthlyResponse = await axiosClient.get(
				"https://api.viralgpt.com/admin/financial-range",
				{
					params: { startDate, endDate, renewal: 'false', subscription: 'true', topup: 'false' },
				},
			);

			const inputs = {};
			response.data.forEach((item) => {
				// Store the raw ad spend without the fee
				inputs[item.date] = parseFloat((item.adSpend / 1.1).toFixed(2));
			});
			setAdSpendInputs(inputs);

			// Process data to separate ad fees
			const processedData = response.data.map((item) => {
				const rawAdSpend = parseFloat((item.adSpend / 1.1).toFixed(2)); // Remove the 10% fee to get the raw ad spend
				// Recompute derived fields locally so the refund/chargeback rate toggle applies
				return recalcRow(item, rawAdSpend);
			});

			// Process monthly data for Total Estimated Revenue
			const processedMonthlyData = monthlyResponse.data.map((item) => {
				const rawAdSpend = parseFloat((item.adSpend / 1.1).toFixed(2));
				return recalcRow(item, rawAdSpend);
			});

			// Sort data with today's date first, then remaining dates in descending order
			const today = moment().format("YYYY-MM-DD");
			const sortedData = [...processedData].sort((a, b) => {
				if (a.date === today) return -1;
				if (b.date === today) return 1;
				return moment(b.date).diff(moment(a.date)); // Descending order for other dates
			});

			const sortedMonthlyData = [...processedMonthlyData].sort((a, b) => {
				if (a.date === today) return -1;
				if (b.date === today) return 1;
				return moment(b.date).diff(moment(a.date));
			});

			setFinancialData(sortedData);
			setMonthlyData(sortedMonthlyData);
		} catch (error) {
			console.error("Error fetching financial data:", error);
			setError("Failed to load financial data. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	// Recalculate all derived fields for a row given a new raw ad spend
	const recalcRow = (item, rawAdSpend) => {
		const adFees = rawAdSpend * adFeeRate;
		const grossRevenue = item.grossRevenue;
		const fees = grossRevenue * 0.05;
		const chargebacks = grossRevenue * riskRate;
		const refunds = grossRevenue * riskRate;
		const productCosts = (item.topupRevenue || 0) * 0.05;
		const refillCosts = item.refillCosts || 0;
		const freeBoostCosts = item.freeBoostCosts || 0;
		const subCosts = (item.allInitialRevenue || 0) * 0.1;
		const netRevenue = grossRevenue - productCosts - refillCosts - freeBoostCosts - rawAdSpend - adFees - fees - subCosts - refunds - chargebacks;
		return {
			...item,
			adSpend: rawAdSpend,
			adFees,
			fees,
			chargebacks,
			refunds,
			productCosts,
			netRevenue,
		};
	};

	// Update ad spend for a specific date
	const updateAdSpend = async (date) => {
		try {
			setError("");

			const adSpend = parseFloat(adSpendInputs[date]);
			if (isNaN(adSpend)) {
				setError("Please enter a valid number for ad spend.");
				return;
			}

			// We still send the total amount (ad spend + 10% fee) to the backend
			const adSpendWithFee = adSpend * 1.1;

			await axiosClient.post("https://api.viralgpt.com/admin/update-ad-spend", {
				date,
				adSpend: adSpendWithFee.toFixed(2),
			});

			setSuccess(`Ad spend updated successfully for ${date}`);
			setTimeout(() => setSuccess(""), 3000);

			// Update and recalculate the entire row in state
			setFinancialData(prevData =>
				prevData.map(item =>
					item.date === date ? recalcRow(item, adSpend) : item
				)
			);

			setMonthlyData(prevData =>
				prevData.map(item =>
					item.date === date ? recalcRow(item, adSpend) : item
				)
			);
		} catch (error) {
			console.error("Error updating ad spend:", error);
			setError("Failed to update ad spend. Please try again.");
		}
	};

	// Handle input change for ad spend
	const handleAdSpendChange = (date, value) => {
		setAdSpendInputs((prev) => ({
			...prev,
			[date]: value,
		}));
	};

	// Calculate totals for the footer
	const calculateTotals = () => {
		if (!financialData.length) return {};

		return financialData.reduce((totals, day) => {
			return {
				revenue: (totals.revenue || 0) + (parseFloat(day.revenue) || 0),
				fbRevenue: (totals.fbRevenue || 0) + (parseFloat(day.fbRevenue) || 0),
				grossRevenue:
					(totals.grossRevenue || 0) + (parseFloat(day.grossRevenue) || 0),
				productCosts:
					(totals.productCosts || 0) + (parseFloat(day.productCosts) || 0),
				refillCosts:
					(totals.refillCosts || 0) + (parseFloat(day.refillCosts) || 0),
				freeBoostCosts:
					(totals.freeBoostCosts || 0) + (parseFloat(day.freeBoostCosts) || 0),
				freeBoostCount:
					(totals.freeBoostCount || 0) + (parseInt(day.freeBoostCount) || 0),
				freeBoostFollowers:
					(totals.freeBoostFollowers || 0) + (parseInt(day.freeBoostFollowers) || 0),
				adSpend: (totals.adSpend || 0) + (parseFloat(day.adSpend) || 0),
				adFees: (totals.adFees || 0) + (parseFloat(day.adFees) || 0),
				fees: (totals.fees || 0) + (parseFloat(day.fees) || 0),
				chargebacks: (totals.chargebacks || 0) + (parseFloat(day.chargebacks) || 0),
				refunds: (totals.refunds || 0) + (parseFloat(day.refunds) || 0),
				netRevenue:
					(totals.netRevenue || 0) + (parseFloat(day.netRevenue) || 0),
				newSubscribers:
					(totals.newSubscribers || 0) + (parseInt(day.newSubscribers) || 0),
			monthlyInitialRevenue:
				(totals.monthlyInitialRevenue || 0) + (parseFloat(day.monthlyInitialRevenue) || 0),
			allInitialRevenue:
				(totals.allInitialRevenue || 0) + (parseFloat(day.allInitialRevenue) || 0),
			topupRevenue:
				(totals.topupRevenue || 0) + (parseFloat(day.topupRevenue) || 0),
			subscriptionRevenue:
				(totals.subscriptionRevenue || 0) + (parseFloat(day.subscriptionRevenue) || 0),
			monthlySubRevenue:
				(totals.monthlySubRevenue || 0) + (parseFloat(day.monthlySubRevenue) || 0),
			annualSubRevenue:
				(totals.annualSubRevenue || 0) + (parseFloat(day.annualSubRevenue) || 0),
			upsellRevenue:
				(totals.upsellRevenue || 0) + (parseFloat(day.upsellRevenue) || 0),
			oneTimeRevenue:
				(totals.oneTimeRevenue || 0) + (parseFloat(day.oneTimeRevenue) || 0),
			oneTimeCount:
				(totals.oneTimeCount || 0) + (parseInt(day.oneTimeCount) || 0),
			changePlanRevenue:
				(totals.changePlanRevenue || 0) + (parseFloat(day.changePlanRevenue) || 0),
			recurringRevenue:
				(totals.recurringRevenue || 0) + (parseFloat(day.recurringRevenue) || 0),
			invoiceRevenue:
				(totals.invoiceRevenue || 0) + (parseFloat(day.invoiceRevenue) || 0),
			initialRevenue:
				(totals.initialRevenue || 0) + (parseFloat(day.initialRevenue) || 0),
			monthlySubCount:
				(totals.monthlySubCount || 0) + (parseInt(day.monthlySubCount) || 0),
			annualSubCount:
				(totals.annualSubCount || 0) + (parseInt(day.annualSubCount) || 0),
		};
	}, {});
};

	// Calculate totals for monthly data (for Total Estimated Revenue)
	const calculateMonthlyTotals = () => {
		if (!monthlyData.length) return {};

		return monthlyData.reduce((totals, day) => {
			return {
				monthlyInitialRevenue:
					(totals.monthlyInitialRevenue || 0) + (parseFloat(day.monthlyInitialRevenue) || 0),
				topupRevenue:
					(totals.topupRevenue || 0) + (parseFloat(day.topupRevenue) || 0),
				productCosts:
					(totals.productCosts || 0) + (parseFloat(day.productCosts) || 0),
				refillCosts:
					(totals.refillCosts || 0) + (parseFloat(day.refillCosts) || 0),
				freeBoostCosts:
					(totals.freeBoostCosts || 0) + (parseFloat(day.freeBoostCosts) || 0),
				adSpend: (totals.adSpend || 0) + (parseFloat(day.adSpend) || 0),
				adFees: (totals.adFees || 0) + (parseFloat(day.adFees) || 0),
				fees: (totals.fees || 0) + (parseFloat(day.fees) || 0),
				chargebacks: (totals.chargebacks || 0) + (parseFloat(day.chargebacks) || 0),
				refunds: (totals.refunds || 0) + (parseFloat(day.refunds) || 0),
			};
		}, {});
	};

	// Helper function to get monthly data for a specific date
	const getMonthlyDataForDate = (date) => {
		return monthlyData.find(item => item.date === date) || {};
	};

	// Format currency values
	const formatCurrency = (value) => {
		const isNegative = (value || 0) < 0;
		const absValue = Math.abs(value || 0);
		const formatted = new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 2,
		}).format(absValue);
		return isNegative ? `$-${formatted.slice(1)}` : formatted;
	};

	// Re-fetch when dates or toggles change
	useEffect(() => {
		fetchFinancialData();
	}, [startDate, endDate, showRenewal, showMonthly, showTopup]);

	// Recompute refund/chargeback estimates in place when the rate toggle changes
	useEffect(() => {
		setFinancialData((prev) => prev.map((item) => recalcRow(item, item.adSpend)));
		setMonthlyData((prev) => prev.map((item) => recalcRow(item, item.adSpend)));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [riskRate, adFeeRate]);

	// Keep toggle states in the URL so a refresh restores them
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		params.set("renewal", showRenewal ? "1" : "0");
		params.set("subscription", showMonthly ? "1" : "0");
		params.set("topup", showTopup ? "1" : "0");
		params.set("lowRisk", lowRiskRates ? "1" : "0");
		params.set("noAdFees", noAdFees ? "1" : "0");
		params.set("simRetention", simRetention ? "1" : "0");
		window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
	}, [showRenewal, showMonthly, showTopup, lowRiskRates, noAdFees, simRetention]);

	const totals = calculateTotals();
	const monthlyTotals = calculateMonthlyTotals();

	return (
		<Card className="mb-4">
			<Card.Body className="p-0">
				{" "}
				{/* Reduce padding on mobile */}
				<Card.Title className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 p-3">
					<span>Financial Funnel</span>
					<div className="d-flex gap-2 mt-2 mt-sm-0 w-100 w-sm-auto">
						<Form.Group as={Row} className="align-items-center w-100">
							<Form.Label column sm="auto">
								Date Range:
							</Form.Label>
							<Col sm="auto">
								<Form.Control
									type="date"
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
								/>
							</Col>
							<Col sm="auto">to</Col>
							<Col sm="auto">
								<Form.Control
									type="date"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
								/>
							</Col>
							<Col sm="auto">
								<Button variant="outline-primary" onClick={fetchFinancialData}>
									Refresh
								</Button>
							</Col>
							<Col sm="auto">
								<div className="d-flex align-items-center gap-3 flex-wrap">
									<Form.Check
										type="checkbox"
										id="renewal-toggle"
										label="Renewal"
										checked={showRenewal}
										onChange={() => setShowRenewal(!showRenewal)}
									/>
									<Form.Check
										type="checkbox"
										id="monthly-toggle"
										label="Subscription"
										checked={showMonthly}
										onChange={() => setShowMonthly(!showMonthly)}
									/>
									<Form.Check
										type="checkbox"
										id="topup-toggle"
										label="Top-Up"
										checked={showTopup}
										onChange={() => setShowTopup(!showTopup)}
									/>
									<Form.Check
										type="switch"
										id="risk-rate-toggle"
										label="5% Refund/CB"
										title="Estimate refunds and chargebacks at 5% of gross each instead of 15%"
										checked={lowRiskRates}
										onChange={() => setLowRiskRates(!lowRiskRates)}
									/>
									<Form.Check
										type="switch"
										id="no-ad-fees-toggle"
										label="No Ad Fees"
										title="Exclude the 10% ad cost fee from net revenue calculations"
										checked={noAdFees}
										onChange={() => setNoAdFees(!noAdFees)}
									/>
									<Form.Check
										type="switch"
										id="sim-retention-toggle"
										label="40% M1 Retention"
										title="Simulate M1-M4 projections with a cohort retaining 40% after month 1 (scales the observed retention curve)"
										checked={simRetention}
										onChange={() => setSimRetention(!simRetention)}
									/>
								</div>
							</Col>
						</Form.Group>
					</div>
				</Card.Title>
				{error && <Alert variant="danger">{error}</Alert>}
				{success && <Alert variant="success">{success}</Alert>}
				{loading ? (
					<div className="text-center py-4">
						<Spinner animation="border" role="status">
							<span className="visually-hidden">Loading...</span>
						</Spinner>
					</div>
				) : (
					<div className="table-responsive">
						<Table striped bordered hover className="financial-table">
							<thead className="bg-light">
								<tr>
									<th>Date</th>
									<th>Revenue</th>

									<th>Gross Revenue</th>
									<th>Product Costs</th>
									<th>Ad Costs</th>

									<th>Processor Costs</th>
									<th>Net Revenue</th>
									<th>New Subs</th>
									<th>Monthly ncLTNP</th>
								</tr>
							</thead>
							<tbody style={{ verticalAlign: 'middle' }}>
								{financialData.map((day) => {
									const monthlyDay = getMonthlyDataForDate(day.date);
									return (
										<tr
											key={day.date}
											className={isToday(day.date) ? "today-highlight" : ""}
										>
											<td>
												{isToday(day.date) ? (
													<span className="today-">
														{moment(day.date).format("MMM DD, YYYY")}
													</span>
												) : (
													moment(day.date).format("MMM DD, YYYY")
												)}
											</td>
											<td>
												<span className="popup-wrapper">
													<strong>Sub:</strong> {formatCurrency(day.initialRevenue)}
													<div className="popup-card">
														<div>Monthly: {formatCurrency(day.monthlySubRevenue)}</div>
														<div>Annual: {formatCurrency(day.annualSubRevenue)}</div>
														<div>Upsell: {formatCurrency(day.upsellRevenue)}</div>
														<div>Plan Change: {formatCurrency(day.changePlanRevenue)}</div>
													</div>
												</span>
												<br />
												<span className="popup-wrapper">
													<strong>Renewal:</strong> {formatCurrency(day.subscriptionRevenue)}
													<div className="popup-card">
														<div>Recurring: {formatCurrency(day.recurringRevenue)}</div>
														<div>Invoice: {formatCurrency(day.invoiceRevenue)}</div>
													</div>
												</span>
												<br />
												{/* Top-ups and one-time packages are both single purchases
												    that never renew, so they are shown together and kept out
												    of Sub, which now means subscriptions only. */}
												<span className="popup-wrapper">
													<strong>TopUp:</strong> {formatCurrency((day.topupRevenue || 0) + (day.oneTimeRevenue || 0))}
													<div className="popup-card">
														<div>Top-ups: {formatCurrency(day.topupRevenue)}</div>
														<div>One Time: {formatCurrency(day.oneTimeRevenue)}</div>
														<div>Packages sold: {day.oneTimeCount || 0}</div>
													</div>
												</span>
											</td>

											<td style={{ backgroundColor: '#ffff00', fontWeight: 'bold' }}>{formatCurrency(day.grossRevenue)}</td>
										<td>
											<span className="popup-wrapper">
												<strong>Sub:</strong> -{formatCurrency((day.allInitialRevenue || 0) * 0.1)}
												<div className="popup-card">
													<div>Monthly: -{formatCurrency((day.monthlySubRevenue || 0) * 0.1)}</div>
													<div>Annual: -{formatCurrency((day.annualSubRevenue || 0) * 0.1)}</div>
												</div>
											</span>
											{showTopup && (
												<>
													<br />
													<strong>TopUp:</strong> -{formatCurrency(day.productCosts)}
												</>
											)}
											<br />
											<strong>Refill:</strong> -{formatCurrency(day.refillCosts)}
											<br />
											<span className="popup-wrapper">
												<strong>Boosts:</strong> -{formatCurrency(day.freeBoostCosts)}
												<div className="popup-card">
													<div>Claims: {day.freeBoostCount || 0}</div>
													<div>Followers: {(day.freeBoostFollowers || 0).toLocaleString()}</div>
												</div>
											</span>
										</td>
											<td>
												<span className="ad-spend-wrapper">
													<span className="ad-spend-prefix">$</span>
													<input
														type="number"
														min="0"
														step="0.01"
														value={adSpendInputs[day.date] ?? ""}
														onChange={(e) =>
															handleAdSpendChange(day.date, e.target.value)
														}
														onBlur={() => updateAdSpend(day.date)}
														onKeyDown={(e) => {
															if (e.key === "Enter") {
																e.target.blur();
															}
														}}
														className="ad-spend-input"
													/>
												</span>
												<br />
												Fees: -{formatCurrency(day.adFees)}
											</td>
											<td>
												<strong>Fees:</strong>	<br />
												-{formatCurrency(day.fees)}
												<br />
												<strong>CBs:</strong>	<br />
												-{formatCurrency(day.chargebacks)}
												<br />
												<strong>Refunds:</strong>	<br />
												-{formatCurrency(day.refunds)}

											</td>
											<td
												style={{
													backgroundColor: day.netRevenue >= 0 ? 'lime' : 'lime'
												}}
											>
												<strong>{formatCurrency(day.netRevenue)}</strong>
											</td>
											<td>
												<span className="popup-wrapper">
													+{day.newSubscribers}
													<div className="popup-card">
														<div>Monthly: {day.monthlySubCount} ({day.newSubscribers ? Math.round(day.monthlySubCount / day.newSubscribers * 100) : 0}%)</div>
														<div>Annual: {day.annualSubCount} ({day.newSubscribers ? Math.round(day.annualSubCount / day.newSubscribers * 100) : 0}%)</div>
													</div>
												</span>
												<br />
												CPS: {formatCurrency(day.newSubscribers ? day.adSpend / day.newSubscribers : 0)}
												<br />
												AOV: {formatCurrency(day.newSubscribers ? ((day.grossRevenue || 0) - (day.subscriptionRevenue || 0)) / day.newSubscribers : 0)}
											</td>
											<td>


												{(() => {
													// Use monthly data for Total Estimated Revenue calculations
													const mDay = monthlyDay;
													const mMonthlyInitialRevenue = mDay.monthlyInitialRevenue || 0;
													const mProductCosts = mDay.productCosts || 0;
													const mAdSpend = mDay.adSpend || 0;
													const mAdFees = mDay.adFees || 0;
													const mFees = mDay.fees || 0;
													const mChargebacks = mDay.chargebacks || 0;
													const mRefunds = mDay.refunds || 0;
													const M0 = day.netRevenue;
													//const M0 = mMonthlyInitialRevenue - mProductCosts - mAdSpend - mAdFees - mFees - mChargebacks - mRefunds - (mMonthlyInitialRevenue * 0.1);
													console.log("Initial Revenue:", mMonthlyInitialRevenue);
													console.log("Product Costs:", mProductCosts);
													console.log("AdSpend:", mAdSpend);
													console.log("Ad Fees:", mAdFees);
													console.log("Fees:", mFees);
													console.log("Chargebacks:", mChargebacks);
													console.log("Refunds:", mRefunds);
													console.log("M0:", M0);

													// Projected profit per month: net of Product Costs (10%) and
													// Fees [CBs & Refunds], which follow the risk-rate toggle.
													const [M1, M2, M3, M4] = retentionCurve.reduce((acc, rate) => {
														const prev = acc.length ? acc[acc.length - 1] : M0;
														const rebill = mMonthlyInitialRevenue * rate;
														acc.push(prev + rebill - (rebill * 0.1) - (rebill * riskRate));
														return acc;
													}, []);
													return (
														<>



															M0: {formatCurrency(M0)}
															<br />
															M1: {formatCurrency(M1)}
															<br />
															M2: {formatCurrency(M2)}
															<br />
															M3: {formatCurrency(M3)}
															<br />
															M4: {formatCurrency(M4)}
														</>
													);
												})()}
											</td>
										</tr>
									)
								})}
							</tbody>
							<tfoot className="table-dark">
								<tr>
									<td>
										<strong>TOTALS</strong>
									</td>
								<td>
									<span className="popup-wrapper">
										<strong>Sub:</strong> {formatCurrency(totals.initialRevenue)}
										<div className="popup-card">
											<div>Monthly: {formatCurrency(totals.monthlySubRevenue)}</div>
											<div>Annual: {formatCurrency(totals.annualSubRevenue)}</div>
											<div>Upsell: {formatCurrency(totals.upsellRevenue)}</div>
											<div>Plan Change: {formatCurrency(totals.changePlanRevenue)}</div>
										</div>
									</span>
									<br />
									<span className="popup-wrapper">
										<strong>Renewal:</strong> {formatCurrency(totals.subscriptionRevenue)}
										<div className="popup-card">
											<div>Recurring: {formatCurrency(totals.recurringRevenue)}</div>
											<div>Invoice: {formatCurrency(totals.invoiceRevenue)}</div>
										</div>
									</span>
									<br />
									<span className="popup-wrapper">
										<strong>TopUp:</strong> {formatCurrency((totals.topupRevenue || 0) + (totals.oneTimeRevenue || 0))}
										<div className="popup-card">
											<div>Top-ups: {formatCurrency(totals.topupRevenue)}</div>
											<div>One Time: {formatCurrency(totals.oneTimeRevenue)}</div>
											<div>Packages sold: {totals.oneTimeCount || 0}</div>
										</div>
									</span>
								</td>
								<td>{formatCurrency(totals.grossRevenue)}</td>

								<td>
									<span className="popup-wrapper">
										<strong>Sub:</strong> -{formatCurrency((totals.allInitialRevenue || 0) * 0.1)}
										<div className="popup-card">
											<div>Monthly: -{formatCurrency((totals.monthlySubRevenue || 0) * 0.1)}</div>
											<div>Annual: -{formatCurrency((totals.annualSubRevenue || 0) * 0.1)}</div>
										</div>
									</span>
									{showTopup && (
										<>
											<br />
											<strong>TopUp:</strong> -{formatCurrency(totals.productCosts)}
										</>
									)}
									<br />
									<strong>Refill:</strong> -{formatCurrency(totals.refillCosts)}
									<br />
									<span className="popup-wrapper">
										<strong>Boosts:</strong> -{formatCurrency(totals.freeBoostCosts)}
										<div className="popup-card">
											<div>Claims: {totals.freeBoostCount || 0}</div>
											<div>Followers: {(totals.freeBoostFollowers || 0).toLocaleString()}</div>
										</div>
									</span>
								</td>
									<td>-{formatCurrency(totals.adSpend)}
										<br />
										Fees:
										-{formatCurrency(totals.adFees)}
									</td>
									<td>
										<strong>Fees:</strong>	<br />
										-{formatCurrency(totals.fees)}
										<br />
										<strong>Chargebacks:</strong>	<br />
										-{formatCurrency(totals.chargebacks)}
										<br />
										<strong>Refunds:</strong>	<br />
										-{formatCurrency(totals.refunds)}
									</td>
									<td
										className={
											totals.netRevenue >= 0 ? "text-success" : "text-danger"
										}
									>
										<strong>{formatCurrency(totals.netRevenue)}</strong>
									</td>
									<td>
										<span className="popup-wrapper">
											+{totals.newSubscribers}
											<div className="popup-card">
												<div>Monthly: {totals.monthlySubCount} ({totals.newSubscribers ? Math.round(totals.monthlySubCount / totals.newSubscribers * 100) : 0}%)</div>
												<div>Annual: {totals.annualSubCount} ({totals.newSubscribers ? Math.round(totals.annualSubCount / totals.newSubscribers * 100) : 0}%)</div>
											</div>
										</span>
										<br />
										CPS: {formatCurrency(totals.newSubscribers ? totals.adSpend / totals.newSubscribers : 0)}
										<br />
										AOV: {formatCurrency(totals.newSubscribers ? ((totals.grossRevenue || 0) - (totals.subscriptionRevenue || 0)) / totals.newSubscribers : 0)}
									</td>
									<td>

										{(() => {
											// Use monthly totals for Total Estimated Revenue calculations
											const urlParams = new URLSearchParams(window.location.search);
											var M0 = totals.netRevenue;
											//const M0 = monthlyTotals.monthlyInitialRevenue - (monthlyTotals.monthlyInitialRevenue * 0.1) - monthlyTotals.adSpend - (monthlyTotals.monthlyInitialRevenue * 0.15);
											const [M1, M2, M3, M4] = retentionCurve.reduce((acc, rate) => {
												const prev = acc.length ? acc[acc.length - 1] : M0;
												const rebill = (monthlyTotals.monthlyInitialRevenue || 0) * rate;
												acc.push(prev + rebill - (rebill * 0.1) - (rebill * riskRate));
												return acc;
											}, []);
											return (
												<>
													M0: {formatCurrency(M0)}
													<br />
													M1: {formatCurrency(M1)}
													<br />
													M2: {formatCurrency(M2)}
													<br />
													M3: {formatCurrency(M3)}
													<br />
													M4: {formatCurrency(M4)}

												</>
											);
										})()}
									</td>
								</tr>
							</tfoot>

						</Table>
						<style jsx="true">{`
              .popup-wrapper {
                position: relative;
                cursor: pointer;
              }
              .popup-card {
                display: none;
                position: absolute;
                left: 0;
                top: 100%;
                z-index: 1000;
                background: #fff;
                border: 1px solid #dee2e6;
                border-radius: 6px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                padding: 8px 12px;
                white-space: nowrap;
                font-size: 12px;
                color: #333;
              }
              .popup-wrapper:hover .popup-card {
                display: block;
              }
              .table-dark .popup-card {
                background: #2c2c2c;
                border-color: #444;
                color: #eee;
              }
              .today-label {
                font-weight: bold;
                color: #daa520;
              }
              td.new-subscribers {
                font-weight: bold;
                color: #daa520;
              }
              .ad-spend-wrapper {
                display: inline-flex;
                align-items: center;
                border: 1px solid #ced4da;
                border-radius: 4px;
                background: #fff;
                overflow: hidden;
                max-width: 110px;
              }
              .ad-spend-wrapper:focus-within {
                border-color: #86b7fe;
                box-shadow: 0 0 0 2px rgba(13,110,253,0.25);
              }
              .ad-spend-prefix {
                padding: 2px 0 2px 6px;
                color: #6c757d;
                user-select: none;
                flex-shrink: 0;
              }
              .ad-spend-input {
                width: 100%;
                border: none;
                outline: none;
                padding: 2px 6px 2px 2px;
                font-size: inherit;
                background: transparent;
                -moz-appearance: textfield;
              }
              .ad-spend-input::-webkit-outer-spin-button,
              .ad-spend-input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
              }

              /* Mobile adjustments to show all columns without horizontal scrolling */
              @media (max-width: 576px) {
                /* Remove horizontal scrolling */
                .table-responsive {
                  overflow-x: visible !important;
                }
                /* Force the table to fit within the screen */
                .financial-table {
                  table-layout: fixed;
                  width: 100%;
                  font-size: 6px;
                }
                @media (min-width: 576px) {
                  .financial-table {
                    font-size: 12px;
                  }
                }
                .ad-spend-wrapper {
                  max-width: 55px;
                }
                .ad-spend-prefix {
                  padding: 1px 0 1px 2px;
                }
                .ad-spend-input {
                  padding: 1px 2px 1px 1px;
                }
                .financial-table th,
                .financial-table td {
                  padding: 0;
                  text-align: center;
                  white-space: normal;
                  overflow: visible;
                  text-overflow: unset;
                  word-wrap: break-word;
                  vertical-align: middle;
                  display: table-cell;
                }
              }
            `}</style>
					</div>
				)}
			</Card.Body>
			{retentionCurve.map((rate, i) => (
				<p key={i}>Projected Profit Month {i + 1} = (MSub Revenue * {rate.toFixed(3)}) - ((MSub Revenue * {rate.toFixed(3)}) * 0.1) - ((MSub Revenue * {rate.toFixed(3)}) * {riskRate})</p>
			))}
		</Card>
	);
};

export default FinancialFunnel;
